import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Archive,
  Search,
  Clock,
  CheckCircle,
  AlertTriangle,
  Building2,
  User,
  Eye,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../utils/config';
import { openAttachment } from '../../utils/openAttachment';


const TicketArchivio = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  const ITEMS_PER_PAGE = 20;
  const token = localStorage.getItem('hw_token');

  useEffect(() => {
    fetchArchivedTickets();
  }, [page]);

  const fetchArchivedTickets = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/archived?skip=${page * ITEMS_PER_PAGE}&limit=${ITEMS_PER_PAGE}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTickets(data.tickets || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching archived tickets:', err);
      toast.error('Errore nel caricamento archivio');
    } finally {
      setLoading(false);
    }
  };

  const loadTicketDetail = async (ticketId) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/${ticketId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedTicket(data.ticket);
        setTicketMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error loading ticket detail:', err);
      toast.error('Errore nel caricamento dettaglio');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleReopenTicket = async (ticketId) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/${ticketId}/status?status=aperto`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Ticket riaperto');
        setSelectedTicket(null);
        fetchArchivedTickets();
      }
    } catch (err) {
      toast.error('Errore nella riapertura');
    }
  };

  const getPriorityColor = (priority) => {
    return priority === 'urgente' 
      ? 'bg-red-100 border-red-300 text-red-800' 
      : 'bg-blue-50 border-blue-200 text-blue-800';
  };

  const getSenderStyle = (senderType) => {
    switch (senderType) {
      case 'responsabile': return 'bg-blue-50 border-blue-200';
      case 'proprieta': return 'bg-green-50 border-green-200';
      case 'ufficio': return 'bg-purple-50 border-purple-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getSenderLabel = (senderType) => {
    switch (senderType) {
      case 'responsabile': return 'Responsabile';
      case 'proprieta': return 'Proprietario';
      case 'ufficio': return 'Ufficio';
      default: return senderType;
    }
  };

  const filteredTickets = tickets.filter(t => 
    searchTerm === '' ||
    t.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.property_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.created_by_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Archive className="h-8 w-8 text-slate-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Archivio Ticket</h1>
              <p className="text-sm text-slate-500">Ticket chiusi e risolti</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchArchivedTickets} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Cerca per oggetto, immobile, responsabile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="mb-4 p-3 bg-slate-100 rounded-lg">
          <span className="text-sm text-slate-600">
            Totale ticket archiviati: <strong>{total}</strong>
          </span>
        </div>

        {/* Tickets List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <Card className="p-8 text-center text-slate-500">
            <Archive className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>Nessun ticket archiviato trovato</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredTickets.map((ticket) => (
              <Card
                key={ticket.id}
                className={`p-4 cursor-pointer hover:shadow-md transition-shadow border-l-4 ${getPriorityColor(ticket.priority)}`}
                onClick={() => loadTicketDetail(ticket.id)}
                data-testid={`archived-ticket-${ticket.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="font-medium text-slate-800">{ticket.subject}</span>
                      {ticket.priority === 'urgente' && (
                        <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded">URGENTE</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {ticket.property_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {ticket.created_by_name}
                      </span>
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-400">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Chiuso: {ticket.closed_at ? new Date(ticket.closed_at).toLocaleDateString('it-IT') : '-'}
                    </div>
                    <div className="mt-1">
                      Da: {ticket.closed_by || '-'}
                    </div>
                    <div className="mt-1 text-slate-500">
                      {ticket.message_count} messaggi
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-600">
              Pagina {page + 1} di {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Detail Modal */}
        {selectedTicket && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTicket(null)}>
            <Card className="w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              {loadingDetail ? (
                <div className="p-8 flex justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="p-4 border-b bg-slate-50 flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">CHIUSO</span>
                        {selectedTicket.priority === 'urgente' && (
                          <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">URGENTE</span>
                        )}
                      </div>
                      <h2 className="text-lg font-bold text-slate-800">{selectedTicket.subject}</h2>
                      <p className="text-sm text-slate-500">
                        {selectedTicket.property_name} - {selectedTicket.created_by_name}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Chiuso il {selectedTicket.closed_at ? new Date(selectedTicket.closed_at).toLocaleString('it-IT') : '-'} da {selectedTicket.closed_by}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedTicket(null)}>
                      <X className="h-5 w-5" />
                    </Button>
                  </div>

                  {/* Timeline */}
                  {selectedTicket.timeline?.length > 0 && (
                    <div className="px-4 py-2 border-b bg-slate-50">
                      <details className="text-xs">
                        <summary className="cursor-pointer text-slate-600 hover:text-slate-800 font-medium">
                          Cronologia eventi ({selectedTicket.timeline.length})
                        </summary>
                        <div className="mt-2 space-y-1 pl-4 border-l-2 border-slate-200">
                          {selectedTicket.timeline.map((event, idx) => (
                            <div key={idx} className="text-slate-500">
                              <span className="font-medium">
                                {event.action === 'created' && 'Creato'}
                                {event.action === 'read_by_owner' && 'Letto da proprietario'}
                                {event.action === 'read_by_office' && 'Letto da ufficio'}
                                {event.action === 'read_by_responsible' && 'Letto da responsabile'}
                                {event.action === 'reply_by_owner' && 'Risposta proprietario'}
                                {event.action === 'reply_by_office' && 'Risposta ufficio'}
                                {event.action === 'closed' && 'Chiuso'}
                                {event.action === 'reopened' && 'Riaperto'}
                                {event.action === 'email_delivery' && 'Notifiche email'}
                              </span>
                              {event.action !== 'email_delivery' && (
                                <>
                                  <span className="ml-2">{event.actor_name || event.actor}</span>
                                  <span className="ml-2 text-slate-400">
                                    {new Date(event.timestamp).toLocaleString('it-IT')}
                                  </span>
                                </>
                              )}
                              {event.action === 'email_delivery' && event.delivery_log && (
                                <div className="ml-4 mt-1 space-y-1">
                                  {event.triggered_by && (
                                    <span className="text-slate-400 text-xs block">Attivato da: {event.triggered_by} - {new Date(event.timestamp).toLocaleString('it-IT')}</span>
                                  )}
                                  {event.delivery_log.map((log, logIdx) => (
                                    <div key={logIdx} className="flex items-center gap-2">
                                      {log.status === 'inviata' && (
                                        <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                                          <span className="h-2 w-2 rounded-full bg-green-500 inline-block"></span>
                                          {log.name} ({log.email})
                                        </span>
                                      )}
                                      {log.status === 'errore' && (
                                        <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                                          <span className="h-2 w-2 rounded-full bg-red-500 inline-block"></span>
                                          {log.name} ({log.email}) - Errore: {log.error}
                                        </span>
                                      )}
                                      {log.status === 'non_inviata' && (
                                        <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                                          <span className="h-2 w-2 rounded-full bg-amber-500 inline-block"></span>
                                          {log.name} - {log.error}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[400px]">
                    {ticketMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`rounded-lg p-3 border ${getSenderStyle(msg.sender_type)}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">
                            {getSenderLabel(msg.sender_type)}
                            {msg.sender_name && ` - ${msg.sender_name}`}
                          </span>
                          <span className="text-xs opacity-70">
                            {new Date(msg.sent_at).toLocaleString('it-IT')}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        {msg.attachments?.length > 0 && (
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {msg.attachments.map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={`Allegato ${idx + 1}`}
                                className="h-16 w-16 object-cover rounded border cursor-pointer"
                                data-testid={`archived-ticket-attachment-${msg.id}-${idx}`}
                                onClick={() => openAttachment(img)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="p-4 border-t bg-white">
                    <Button onClick={() => handleReopenTicket(selectedTicket.id)}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Riapri Ticket
                    </Button>
                  </div>
                </>
              )}
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TicketArchivio;
